/**
 * Support widget — framework-agnostic behavior core.
 *
 * Owns no rendering and no chat implementation. The markup is supplied by the
 * surface (a Django include, a React component, the DS demo partial); the chat
 * plumbing is injected via `openChat`. This module has NO import of, and no
 * reference to, any chat SDK — that is what structurally guarantees zero
 * chat-vendor requests until the first Chat interaction (companion plan §3).
 *
 * There is NO date arithmetic here. Availability arrives resolved in
 * `config.state`; the reveal is a timer over *visible* time, nothing more.
 */
import { SUPPORT_DISMISS_STORAGE_KEY } from "./constants"
import type { DismissMethod, DismissReason, SupportWidgetConfig, SupportWidgetDeps, SupportWidgetHandle } from "./types"

// Root modifier classes (kept in lockstep with _support-widget.scss).
const CLS_REVEALED = "support-widget--revealed"
const CLS_ONLINE = "support-widget--online"
const CLS_OFFLINE = "support-widget--offline"
const CLS_SHEET_OPEN = "support-widget--sheet-open"
const CLS_CALL_REVEALED = "support-widget-sheet--call-revealed"
const CLS_COLLAPSED = "support-widget--collapsed" // suppress hover-expand until the pointer leaves
const CLS_NO_PEEK = "support-widget--no-peek" // #5620: skip the reveal peek, mount as a plain circle

const noop = (): void => {}

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(SUPPORT_DISMISS_STORAGE_KEY) === "1"
  } catch {
    return false // private mode / storage disabled — never block the widget
  }
}

function persistDismissed(): void {
  try {
    window.localStorage.setItem(SUPPORT_DISMISS_STORAGE_KEY, "1")
  } catch {
    /* storage unavailable — dismissal is best-effort */
  }
}

/** A malformed payload must bail quietly, never throw into the page (§9.7). */
function isUsableConfig(config: SupportWidgetConfig | undefined): config is SupportWidgetConfig {
  return (
    !!config &&
    (config.state === "available" || config.state === "offline") &&
    typeof config.delayMs === "number" &&
    !!config.channels
  )
}

export function mountSupportWidget(deps: SupportWidgetDeps): SupportWidgetHandle {
  const {
    root,
    config,
    openChat,
    reveal: strategy = "timer",
    startCollapsed = false,
    onReveal = noop,
    track = noop,
    now = () => new Date(),
  } = deps

  const nowMs = (): number => now().getTime()

  // ---- Guards ------------------------------------------------------------
  if (!root || !isUsableConfig(config) || readDismissed()) {
    return { reveal: noop, openSheet: noop, closeSheet: noop, hide: noop, destroy: noop }
  }

  // ---- Element lookup (data-* hooks, so class names stay presentational) --
  const buttonEl = root.querySelector<HTMLElement>("[data-support-button]")
  const sheetEl = root.querySelector<HTMLElement>("[data-support-sheet]")
  const closeEl = root.querySelector<HTMLElement>("[data-support-close]")
  const backEl = root.querySelector<HTMLElement>("[data-support-back]")
  const chatEl = root.querySelector<HTMLElement>('[data-support-channel="chat"]')
  const phoneEl = root.querySelector<HTMLElement>('[data-support-channel="phone"]')
  const formEl = root.querySelector<HTMLElement>('[data-support-channel="form"]')
  const callListEl = root.querySelector<HTMLElement>("[data-support-call-list]")

  // ---- Mutable state -----------------------------------------------------
  let destroyed = false
  let revealed = false
  let sheetOpen = false
  let revealTimer = 0
  let visibleAccumMs = 0
  let lastVisibleAt = 0
  let onPointerMoveClear: ((event: Event) => void) | null = null
  // Sheet-open bookkeeping for the Dismissed event (#5619): when the sheet opened,
  // and whether a channel was picked before it closed (a channel pick is NOT a
  // dismissal, so the adapter must be able to tell the two apart).
  let openedAtMs = 0
  let channelSelectedThisOpen = false

  /** Seconds the sheet has been open, for the Dismissed event's `seconds_open`. */
  function elapsedOpenSeconds(): number {
    return openedAtMs ? Math.round((nowMs() - openedAtMs) / 1000) : 0
  }

  root.classList.add(config.state === "available" ? CLS_ONLINE : CLS_OFFLINE)

  // Mount straight to the resting circle (#5620): suppress the one-time reveal
  // peek. Set before CLS_REVEALED lands — the peek keyframes fire the instant the
  // root starts matching --revealed, so the guard must already be in place.
  if (startCollapsed) root.classList.add(CLS_NO_PEEK)

  // ---- Reveal (timer counts only while the tab is visible, #5619) --------
  function startRevealTimer(): void {
    if (revealed || revealTimer) return
    if (document.visibilityState !== "visible") return
    lastVisibleAt = nowMs()
    const remaining = Math.max(0, config.delayMs - visibleAccumMs)
    revealTimer = window.setTimeout(doReveal, remaining)
  }

  function pauseRevealTimer(): void {
    if (revealTimer) {
      clearTimeout(revealTimer)
      revealTimer = 0
    }
    if (lastVisibleAt) {
      visibleAccumMs += nowMs() - lastVisibleAt
      lastVisibleAt = 0
    }
  }

  function doReveal(): void {
    if (revealed || destroyed) return
    revealed = true
    if (revealTimer) {
      clearTimeout(revealTimer)
      revealTimer = 0
    }
    root.classList.add(CLS_REVEALED)
    onReveal()
    track("support_widget_revealed")
  }

  // The circle ↔ label-pill transition is pure CSS (:hover), so the core owns no
  // expand/collapse state — a click always opens the sheet.

  // ---- Sheet open / close (listeners bound only while open, §5.4) --------
  function onKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") closeSheet("esc")
  }

  function onOutsidePointer(event: Event): void {
    const target = event.target as Node
    if (sheetEl?.contains(target) || buttonEl?.contains(target)) return
    closeSheet("backdrop")
  }

  function clearForcedCollapse(): void {
    root.classList.remove(CLS_COLLAPSED)
    if (onPointerMoveClear) {
      document.removeEventListener("pointermove", onPointerMoveClear)
      onPointerMoveClear = null
    }
  }

  function openSheet(): void {
    if (sheetOpen || destroyed) return
    clearForcedCollapse()
    sheetOpen = true
    openedAtMs = nowMs()
    channelSelectedThisOpen = false
    root.classList.add(CLS_SHEET_OPEN)
    document.addEventListener("keydown", onKeydown)
    document.addEventListener("pointerdown", onOutsidePointer)
    closeEl?.focus() // user-initiated, so moving focus into the sheet is correct
    track("support_widget_opened")
  }

  /** DOM + listener teardown for the open sheet. No tracking — callers own that. */
  function teardownSheet(): void {
    sheetOpen = false
    root.classList.remove(CLS_SHEET_OPEN)
    sheetEl?.classList.remove(CLS_CALL_REVEALED) // reset to channels view for next open
    document.removeEventListener("keydown", onKeydown)
    document.removeEventListener("pointerdown", onOutsidePointer)
    buttonEl?.focus()

    // Return to a circle even if the cursor is still over the launcher. Keep the
    // suppression only while the pointer stays on the button; clear it the moment
    // it moves off, so a deliberate re-hover expands the pill again.
    clearForcedCollapse()
    root.classList.add(CLS_COLLAPSED)
    onPointerMoveClear = (event: Event): void => {
      if (buttonEl?.contains(event.target as Node)) return
      clearForcedCollapse()
    }
    document.addEventListener("pointermove", onPointerMoveClear)
  }

  /**
   * Close the open sheet and report it. `method` labels how it was closed and
   * `channelSelected` tells the adapter whether a channel was picked first — the
   * adapter maps this to `Support Widget Dismissed` only when it was NOT (#5619).
   */
  function closeSheet(method: DismissMethod = "close_x"): void {
    if (!sheetOpen) return
    const secondsOpen = elapsedOpenSeconds()
    const channelSelected = channelSelectedThisOpen
    teardownSheet()
    openedAtMs = 0
    track("support_widget_closed", { method, secondsOpen, channelSelected })
  }

  // ---- Channel handlers --------------------------------------------------
  // Hover expands the pill (CSS); a click just toggles the sheet.
  function onButtonClick(): void {
    if (sheetOpen) closeSheet("close_x")
    else openSheet()
  }

  function onChatClick(): void {
    // The core does nothing heavy on the click — that is the INP budget.
    channelSelectedThisOpen = true
    openChat()
    track("support_widget_chat")
  }

  function onPhoneClick(event: Event): void {
    // Desktop: switch the sheet to the phone-call view (customer line number).
    // Mobile ALWAYS navigates the tel: link natively — that is the button mobile
    // users actually press, so it must never be intercepted.
    channelSelectedThisOpen = true
    const isDesktopPointer =
      typeof window.matchMedia === "function" && window.matchMedia("(hover: hover) and (pointer: fine)").matches
    if (callListEl && isDesktopPointer) {
      event.preventDefault()
      sheetEl?.classList.add(CLS_CALL_REVEALED)
    }
    track("support_widget_phone")
  }

  function onBack(): void {
    sheetEl?.classList.remove(CLS_CALL_REVEALED) // phone-call view → channels list
  }

  function onFormClick(): void {
    channelSelectedThisOpen = true
    track("support_widget_form")
  }

  // ---- Dismiss / hide ----------------------------------------------------
  function hide(reason: DismissReason = "user"): void {
    // Tear the sheet down silently — hiding the whole widget is a single dismissal,
    // so we must NOT also emit `support_widget_closed` (that would double-count).
    const secondsOpen = sheetOpen ? elapsedOpenSeconds() : 0
    if (sheetOpen) teardownSheet()
    openedAtMs = 0
    root.classList.remove(CLS_REVEALED)
    // Allow a later reveal() to bring it back — needed for auto_hide (#5619),
    // where the widget re-appears once the job form closes. A user dismissal is
    // persisted, so it stays gone on the next page load.
    revealed = false
    if (reason === "user") persistDismissed()
    track("support_widget_dismissed", { reason, secondsOpen })
  }

  // ---- Visibility (drives the reveal timer only) -------------------------
  function onVisibilityChange(): void {
    if (revealed) return
    if (document.visibilityState === "visible") startRevealTimer()
    else pauseRevealTimer()
  }

  // ---- Wiring (each listener records its own teardown, so destroy() can't
  //      drift out of sync with this list) ----------------------------------
  const cleanups: Array<() => void> = []
  function on(el: EventTarget | null, type: string, handler: EventListener): void {
    if (!el) return
    el.addEventListener(type, handler)
    cleanups.push(() => el.removeEventListener(type, handler))
  }

  on(buttonEl, "click", onButtonClick)
  on(closeEl, "click", () => closeSheet("close_x"))
  on(backEl, "click", onBack)
  on(chatEl, "click", onChatClick)
  on(phoneEl, "click", onPhoneClick as EventListener)
  on(formEl, "click", onFormClick)
  on(document, "visibilitychange", onVisibilityChange)

  if (strategy === "immediate") doReveal()
  else if (strategy === "timer") startRevealTimer()
  // 'external' waits for handle.reveal()

  // ---- Handle ------------------------------------------------------------
  function destroy(): void {
    if (destroyed) return
    destroyed = true
    if (revealTimer) clearTimeout(revealTimer)
    if (onPointerMoveClear) document.removeEventListener("pointermove", onPointerMoveClear)
    // Sheet-scoped listeners are attached only while open; remove them defensively.
    document.removeEventListener("keydown", onKeydown)
    document.removeEventListener("pointerdown", onOutsidePointer)
    cleanups.forEach((off) => off())
  }

  return {
    reveal: doReveal,
    openSheet,
    closeSheet,
    hide,
    destroy,
  }
}
